import { useRouter } from 'next/router'
import { useState } from 'react'
import { useQueryClient } from 'react-query'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Checkbox } from '@wartek-id/checkbox-v1'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast-v2'

import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from 'app/KinerjaShared/components/Card'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/Table'
import { tugasPokok } from 'app/KinerjaPage/constants'
import {
  API_PATH_TUGAS_POKOK,
  useAPIGetRealisasiSKP,
  useAPIGetTugasPokok,
  useAPIPelaksanaanProgress,
  useAPISubmitTugasPokok,
} from 'api/kinerja/useAPIKinerja'
import { DEFAULT_SKP_PERIOD } from 'app/KinerjaShared/constants/period'

type PelaksanaanTugasPokokProps = {
  year?: number
  isReadOnly?: boolean
}

export function PelaksanaanTugasPokok({
  year,
  isReadOnly = false,
}: PelaksanaanTugasPokokProps) {
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()

  // Override period params only if the year param exist
  const hasYearParam = !!year
  const periodParams = hasYearParam
    ? {
        // Use period=1 for the new document flow
        skp_period: DEFAULT_SKP_PERIOD,
        skp_year: year,
      }
    : undefined

  const [isChecked, setIsChecked] = useState(false)

  const { data: dataTugasTambahan, isLoading: isLoadingTugasTambahan } =
    useAPIGetRealisasiSKP({
      params: {
        kategori: ['TT'],
        periodParams,
      },
      options: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  const { data: dataTugasPokok, isLoading: isLoadingTugasPokok } =
    useAPIGetTugasPokok({
      params: { periodParams },
      options: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  const { data: dataPelaksanaan } = useAPIPelaksanaanProgress({
    periodParams,
  })

  const tugasPokokMutation = useAPISubmitTugasPokok()

  const state = dataTugasPokok?.data?.state

  const isSubmitted = state === 'SUBMITTED' || state === 'APPROVED'
  const isEvaluated = dataPelaksanaan?.data?.evaluationDocumentState === 'READY'
  const isFormDisabled = isReadOnly || isSubmitted || isEvaluated

  const isLoading =
    isLoadingTugasPokok ||
    isLoadingTugasTambahan ||
    tugasPokokMutation.isLoading

  const handleSubmit = () => {
    if (isFormDisabled) return

    tugasPokokMutation.mutate(
      {},
      {
        onSuccess: () => {
          queryClient.invalidateQueries(API_PATH_TUGAS_POKOK)
          toast({
            message: 'Berhasil selesaikan pelaksanaan!',
            type: 'success',
          })
        },
        onError: () => {
          toast({
            message: 'Gagal selesaikan pelaksanaan. Coba lagi.',
          })
        },
      }
    )
  }

  return (
    <div className="px-8 pb-8 pt-12 bg-gray-10 min-h-full">
      <div className="max-w-7xl mx-auto">
        <Header
          title="Pelaksanaan Tugas Pokok"
          className="mb-9 print:hidden"
          onBack={() => {
            router.back()
          }}
        />

        <div className="flex items-start gap-8">
          <aside className="w-96 flex-shrink-0 flex-grow-0 p-4 flex gap-2 rounded-lg border border-gray-50">
            <Icon fontSize="sm" className="!text-xl !leading-5">
              info
            </Icon>

            <ul className="list-disc pl-6 space-y-2">
              <Text as="li">
                <strong>Tunjukkan bukti pelaksanaan</strong> ke atasan Anda.
              </Text>
              <Text as="li">
                <strong>Tidak perlu kumpulkan Bukti Dukung</strong> untuk
                Pelaksanaan Tugas Pokok.
              </Text>
            </ul>
          </aside>
          <Card withBorder>
            <CardHeader>
              <Text variant="heading-lg" as="h2">
                Tekan pernyataan di bawah jika pelaksanaan sudah selesai
              </Text>
            </CardHeader>

            <CardBody className="p-6 space-y-4">
              <Table withOuterBorder rounded>
                <TableHeader>
                  <TableHeaderItem>No.</TableHeaderItem>
                  <TableHeaderItem>Rencana Hasil Kerja</TableHeaderItem>
                </TableHeader>
                <TableBody>
                  {isLoadingTugasTambahan ? (
                    <TableShimmer columns={2} rows={5} />
                  ) : (
                    <>
                      {tugasPokok.map((item, index) => (
                        <TableRow key={index}>
                          <TableRowItem className="text-center">
                            {index + 1}
                          </TableRowItem>
                          <TableRowItem>{item}</TableRowItem>
                        </TableRow>
                      ))}

                      {dataTugasTambahan?.data?.map((item, index) => (
                        <TableRow key={index}>
                          <TableRowItem className="text-center">
                            {index + tugasPokok.length + 1}
                          </TableRowItem>
                          <TableRowItem>{item.deskripsi}</TableRowItem>
                        </TableRow>
                      ))}
                    </>
                  )}
                </TableBody>
              </Table>

              <div className="bg-surface-selected-default p-4 rounded-lg flex">
                <Checkbox
                  labelPosition="center"
                  className="flex"
                  disabled={isLoading || isFormDisabled}
                  checked={isChecked || isSubmitted}
                  onChange={(event) => {
                    setIsChecked(event.currentTarget.checked)
                  }}
                >
                  <Text as="span" variant="body-sm-bold">
                    Saya sudah melaksanakan Pelaksanaan Tugas Pokok
                  </Text>
                </Checkbox>
              </div>
            </CardBody>

            {!isFormDisabled && (
              <CardFooter withSeparator={false} className="pt-0">
                <Button
                  size="lg"
                  className="ml-auto"
                  isLoading={isLoading}
                  disabled={isLoading || isFormDisabled || !isChecked}
                  onClick={handleSubmit}
                >
                  {isLoading ? 'Memuat..' : 'Simpan'}
                </Button>
              </CardFooter>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
